/**
 * Pure helpers for a list that pages as the user scrolls, with every loaded
 * page held in ONE cache entry (see `common/hooks/usePagedList`). Generic over
 * the row type; the rules match `features/event/eventStreamPages`, which the
 * schedule event streams use.
 */

export interface PagePagination {
  current_page: number
  total_pages: number
}

export interface Page<T> {
  data: T[]
  /** Null when the response carried no usable pagination — one page, complete. */
  pagination: PagePagination | null
}

/** What one paged list stores under its cache key. */
export interface PagedListData<T> {
  pages: Page<T>[]
}

/**
 * A response's `meta.pagination`, normalised. Falls back to the page that was
 * asked for when the server leaves `current_page` out; null when there is no
 * pagination at all, which reads as one complete page.
 */
export const toPagePagination = (
  raw: { current_page?: unknown; total_pages?: unknown } | null | undefined,
  requestedPage: number
): PagePagination | null => {
  if (!raw) {
    return null
  }
  return {
    current_page: Number(raw.current_page ?? requestedPage),
    total_pages: Number(raw.total_pages ?? 1),
  }
}

/**
 * Whether pages remain to load, read off the LAST loaded page — the only one
 * whose pagination describes where the list stands. No pagination reads as
 * complete: stopping early shows a short list, looping never terminates.
 */
export const hasMorePages = <T>(
  pages: readonly Page<T>[] | null | undefined
): boolean => {
  const last = pages?.[pages.length - 1]?.pagination
  if (!last) {
    return false
  }
  const current = Number(last.current_page ?? 0)
  const total = Number(last.total_pages ?? 0)
  return current > 0 && current < total
}

/**
 * The page number to request next, or null when the list is done. Taken from
 * the last response's own `current_page`, so a server that answers with a
 * different page than asked still advances from where it actually is.
 */
export const nextPageNumber = <T>(
  data: PagedListData<T> | null | undefined
): number | null => {
  const pages = data?.pages
  if (!hasMorePages(pages)) {
    return null
  }
  const last = pages?.[pages.length - 1]?.pagination
  return Number(last?.current_page ?? 0) + 1
}

/**
 * Appends a freshly fetched page. Refuses a page already held: two scroll
 * events can each start a request before either lands, and appending both
 * would show every row on that page twice.
 */
export const appendPage = <T>(
  data: PagedListData<T> | null | undefined,
  page: Page<T>
): PagedListData<T> => {
  const pages = data?.pages ?? []
  const incoming = Number(page?.pagination?.current_page ?? NaN)

  if (Number.isFinite(incoming)) {
    const alreadyHeld = pages.some(
      (held) => Number(held?.pagination?.current_page ?? NaN) === incoming
    )
    if (alreadyHeld) {
      return { pages }
    }
  }

  return { pages: [...pages, page] }
}

/**
 * Whether the last loaded page put nothing on screen: every row it brought is
 * hidden by the caller's own rules (or it brought none). A list in that state
 * should load on — the user is still at its end, and waiting for a scroll that
 * can't happen would read as "no more results" while pages remain.
 */
export const lastPageShowsNothing = (
  lastPageIds: readonly string[],
  visibleIds: ReadonlySet<string>
): boolean => !lastPageIds.some((id) => visibleIds.has(id))
